import React, { useEffect, useMemo, useState } from "react";

const API_URL = "https://cms.amservices.eu/api/mbj_users_manager.php";
const EMPTY_FORM = { id: null, nom: "", username: "", password: "", role: "user" };
const ROLES = {
  user: { label: "Utilisateur", icon: "🟢" },
  admin: { label: "Administrateur", icon: "🔴" },
};

function token() {
  const user = JSON.parse(localStorage.getItem("am_user") || "null");
  return localStorage.getItem("am_token") || user?.token || "";
}

function formatDate(value) {
  if (!value) return "Jamais";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("fr-FR");
}

export default function MbjUsersManager({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const editingAdmin = String(
    users.find(user => Number(user.id) === Number(form.id))?.username || ""
  ).toLowerCase() === "admin";

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter(user =>
      [user.id, user.nom, user.username, user.role].some(value =>
        String(value || "").toLowerCase().includes(query)
      )
    );
  }, [users, search]);

  const request = async (payload = null) => {
    const authToken = token();
    const response = await fetch(API_URL, {
      method: payload ? "POST" : "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Auth-Token": authToken,
      },
      ...(payload ? { body: JSON.stringify(payload) } : {}),
    });
    const text = await response.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error("Réponse invalide du serveur.");
    }
    if (!response.ok || !data.success) {
      throw new Error(data.message || `Erreur HTTP ${response.status}`);
    }
    return data;
  };

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await request();
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (err) {
      setError(err.message || "Impossible de charger les utilisateurs MBJ.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async payload => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await request(payload);
      setUsers(Array.isArray(data.users) ? data.users : []);
      setMessage(data.message || "Opération terminée.");
      return true;
    } catch (err) {
      setError(err.message || "L’opération a échoué.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const edit = user => {
    setForm({
      id: Number(user.id),
      nom: user.nom || "",
      username: user.username || "",
      password: "",
      role: user.role || "user",
    });
    setError("");
    setMessage("");
  };

  const submit = async event => {
    event.preventDefault();
    const done = await run({
      action: form.id ? "update" : "create",
      id: form.id,
      nom: form.nom.trim(),
      username: form.username.trim(),
      password: form.password,
      role: form.role,
    });
    if (done) setForm(EMPTY_FORM);
  };

  const remove = async user => {
    if (!window.confirm(
      `Supprimer ${user.nom || user.username} du portail MBJ, du Planning et des Clients ?`
    )) return;
    const done = await run({ action: "delete", id: Number(user.id) });
    if (done && Number(form.id) === Number(user.id)) setForm(EMPTY_FORM);
  };

  const purgeNonAdmin = async () => {
    if (!window.confirm(
      "Supprimer tous les utilisateurs MBJ sauf admin ? Les interventions seront conservées mais désaffectées."
    )) return;
    if (!window.confirm("Confirmation définitive : conserver uniquement le compte admin ?")) return;
    const done = await run({ action: "purge_non_admin" });
    if (done) setForm(EMPTY_FORM);
  };

  const syncedCount = users.filter(
    user => Number(user.synced_planning) && Number(user.synced_clients) && Number(user.synced_appels)
  ).length;

  return (
    <main className="users-manager-page users-manager-mbj">
      <header className="users-manager-header">
        <div className="users-manager-title">
          <span className="um-eyebrow">Administration · MBJ</span>
          <h1>Utilisateurs MBJ</h1>
          <p>Un compte commun au portail MBJ, au Planning et au module Clients.</p>
        </div>
        <div className="um-current-admin">
          <span>Administrateur CMS connecté</span>
          <strong>{currentUser?.nom || currentUser?.login}</strong>
        </div>
      </header>

      <section className="um-summary" aria-label="Résumé MBJ">
        <article><strong>{users.length}</strong><span>comptes MBJ</span></article>
        <article><strong>{syncedCount}</strong><span>comptes synchronisés</span></article>
        <article><strong>{users.filter(user => user.role === "admin").length}</strong><span>administrateur(s)</span></article>
      </section>

      {users.some(user => String(user.username).toLowerCase() !== "admin") && (
        <section className="um-danger-zone">
          <div>
            <strong>Réinitialiser l’équipe MBJ</strong>
            <span>Conserve uniquement admin et retire les personnes du Planning.</span>
          </div>
          <button className="um-btn um-btn-danger" type="button" onClick={purgeNonAdmin} disabled={saving}>
            Supprimer tous sauf admin
          </button>
        </section>
      )}

      {message && <div className="um-alert um-alert-success">{message}</div>}
      {error && <div className="um-alert um-alert-error">{error}</div>}

      <div className="um-content-grid">
        <section className="um-panel um-form-panel">
          <div className="um-panel-title">
            <div>
              <span className="um-step">{form.id ? `Compte #${form.id}` : "Nouveau compte"}</span>
              <h2>{form.id ? "Modifier l’utilisateur" : "Ajouter un utilisateur MBJ"}</h2>
            </div>
            {form.id && (
              <button className="um-link-button" type="button" onClick={() => setForm(EMPTY_FORM)}>
                Annuler
              </button>
            )}
          </div>

          {editingAdmin && (
            <div className="um-master-notice">
              Le compte administrateur MBJ est protégé.
            </div>
          )}

          <form onSubmit={submit}>
            <div className="um-form-group">
              <label htmlFor="mbj-name">Nom complet</label>
              <input
                id="mbj-name"
                className="um-input"
                value={form.nom}
                onChange={event => setForm({ ...form, nom: event.target.value })}
                required
              />
            </div>
            <div className="um-form-group">
              <label htmlFor="mbj-username">Identifiant</label>
              <input
                id="mbj-username"
                className="um-input"
                value={form.username}
                onChange={event => setForm({ ...form, username: event.target.value })}
                disabled={editingAdmin}
                required
              />
              <span className="um-hint">Cet identifiant sera identique dans les trois modules.</span>
            </div>
            <div className="um-form-group">
              <label htmlFor="mbj-password">
                {form.id ? "Nouveau mot de passe" : "Mot de passe initial"}
              </label>
              <input
                id="mbj-password"
                className="um-input"
                type="password"
                minLength={8}
                value={form.password}
                onChange={event => setForm({ ...form, password: event.target.value })}
                placeholder={form.id ? "Vide = mot de passe inchangé" : "8 caractères minimum"}
                required={!form.id}
                autoComplete="new-password"
              />
            </div>
            <div className="um-form-group">
              <label htmlFor="mbj-role">Rôle portail MBJ</label>
              <select
                id="mbj-role"
                className="um-select"
                value={form.role}
                onChange={event => setForm({ ...form, role: event.target.value })}
                disabled={editingAdmin}
              >
                {Object.entries(ROLES).map(([value, role]) => (
                  <option key={value} value={value}>{role.icon} {role.label}</option>
                ))}
              </select>
            </div>
            <button className="um-btn um-btn-primary" type="submit" disabled={saving}>
              {saving ? "Enregistrement…" : form.id ? "Enregistrer" : "Créer et synchroniser"}
            </button>
          </form>
        </section>

        <section className="um-panel um-list-panel">
          <div className="um-toolbar">
            <div><span className="um-step">MBJ</span><h2>Équipe enregistrée</h2></div>
            <input
              className="um-input um-search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Rechercher…"
              aria-label="Rechercher un utilisateur MBJ"
            />
          </div>

          {loading ? <div className="um-loading">Chargement des comptes MBJ…</div> : (
            <div className="um-table-wrap">
              <table className="um-table">
                <thead>
                  <tr><th>Utilisateur</th><th>Rôle</th><th>Synchronisation</th><th>Dernière connexion</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => {
                    const role = ROLES[user.role] || ROLES.user;
                    const synced = Number(user.synced_planning) && Number(user.synced_clients) && Number(user.synced_appels);
                    const isAdmin = String(user.username).toLowerCase() === "admin";
                    return (
                      <tr key={user.id}>
                        <td>
                          <div className="um-user-cell">
                            <span className="um-avatar mbj">{String(user.nom || user.username).charAt(0).toUpperCase()}</span>
                            <span>
                              <strong>{user.nom} {isAdmin && <em className="um-master-badge">Protégé</em>}</strong>
                              <small>@{user.username} · #{user.id}</small>
                            </span>
                          </div>
                        </td>
                        <td><span className={`um-role um-role-${user.role}`}>{role.icon} {role.label}</span></td>
                        <td>
                          <div className="um-sync-status">
                            <span className={Number(user.synced_planning) ? "is-ok" : "is-missing"}>Planning</span>
                            <span className={Number(user.synced_clients) ? "is-ok" : "is-missing"}>Clients</span>
                            <span className={Number(user.synced_appels) ? "is-ok" : "is-missing"}>Appels</span>
                          </div>
                        </td>
                        <td className="um-date">{formatDate(user.last_login)}</td>
                        <td>
                          <div className="um-row-actions">
                            {!synced && (
                              <button className="um-btn um-btn-sync" type="button" onClick={() => run({ action: "sync", id: Number(user.id) })} disabled={saving}>
                                Synchroniser
                              </button>
                            )}
                            <button className="um-btn um-btn-warning" type="button" onClick={() => edit(user)} disabled={saving}>
                              Modifier
                            </button>
                            {!isAdmin && (
                              <button className="um-btn um-btn-danger" type="button" onClick={() => remove(user)} disabled={saving}>
                                Supprimer
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!filteredUsers.length && (
                    <tr><td colSpan="5" className="um-empty">Aucun utilisateur MBJ trouvé.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
